import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { usePaymentRefunds, useProcessRefund, PaymentRefund } from '@/hooks/usePaymentRefunds';
import { format } from 'date-fns';
import { CheckCircle, XCircle, Clock, DollarSign, AlertCircle } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export function RefundManagementPanel() {
  const [selectedRefund, setSelectedRefund] = useState<PaymentRefund | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [activeTab, setActiveTab] = useState('requested');

  const { data: refunds, isLoading } = usePaymentRefunds({ status: activeTab === 'all' ? undefined : activeTab });
  const { mutate: processRefund, isPending } = useProcessRefund();

  const handleApprove = (refundId: string) => {
    processRefund({ refundId, action: 'approve', adminNotes }, {
      onSuccess: () => {
        setSelectedRefund(null);
        setAdminNotes('');
      },
    });
  };

  const handleReject = (refundId: string) => {
    processRefund({ refundId, action: 'reject', adminNotes }, {
      onSuccess: () => {
        setSelectedRefund(null);
        setAdminNotes('');
      },
    });
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: any; icon: any; label: string }> = {
      requested: { variant: 'default', icon: Clock, label: 'Pending Review' },
      approved: { variant: 'default', icon: CheckCircle, label: 'Approved' },
      processing: { variant: 'default', icon: Clock, label: 'Processing' },
      completed: { variant: 'default', icon: CheckCircle, label: 'Completed' },
      failed: { variant: 'destructive', icon: AlertCircle, label: 'Failed' },
      rejected: { variant: 'destructive', icon: XCircle, label: 'Rejected' },
    };

    const config = variants[status] || variants.requested;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  if (isLoading) {
    return <div>Loading refunds...</div>;
  }

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="requested">Pending</TabsTrigger>
          <TabsTrigger value="completed">Completed</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="space-y-4">
          {refunds?.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <p className="text-center text-muted-foreground">No refund requests found</p>
              </CardContent>
            </Card>
          ) : (
            refunds?.map((refund) => (
              <Card key={refund.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <CardTitle className="text-lg">
                        Refund Request - {refund.refund_type === 'full' ? 'Full' : 'Partial'}
                      </CardTitle>
                      <CardDescription>
                        Requested {format(new Date(refund.requested_at), 'MMM dd, yyyy')}
                      </CardDescription>
                    </div>
                    {getStatusBadge(refund.refund_status)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <div className="text-sm text-muted-foreground">Refund Amount</div>
                      <div className="text-2xl font-bold flex items-center gap-1">
                        <DollarSign className="h-5 w-5" />
                        {(refund.refund_amount_cents / 100).toFixed(2)}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-sm text-muted-foreground">Platform Fee Refund</div>
                      <div className="text-2xl font-bold flex items-center gap-1">
                        <DollarSign className="h-5 w-5" />
                        {(refund.platform_fee_refund_cents / 100).toFixed(2)}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="text-sm font-medium">Reason</div>
                    <Badge variant="outline">{refund.refund_reason.replace('_', ' ')}</Badge>
                  </div>

                  {refund.reason_details && (
                    <div className="space-y-2">
                      <div className="text-sm font-medium">Details</div>
                      <p className="text-sm text-muted-foreground">{refund.reason_details}</p>
                    </div>
                  )}

                  {refund.admin_notes && (
                    <div className="space-y-2">
                      <div className="text-sm font-medium">Admin Notes</div>
                      <p className="text-sm text-muted-foreground">{refund.admin_notes}</p>
                    </div>
                  )}

                  {refund.stripe_refund_id && (
                    <div className="space-y-2">
                      <div className="text-sm font-medium">Stripe Refund ID</div>
                      <code className="text-xs bg-muted px-2 py-1 rounded">{refund.stripe_refund_id}</code>
                    </div>
                  )}

                  {refund.refund_status === 'requested' && (
                    <div className="space-y-4 pt-4 border-t">
                      <div className="space-y-2">
                        <Label htmlFor={`notes-${refund.id}`}>Admin Notes (Optional)</Label>
                        <Textarea
                          id={`notes-${refund.id}`}
                          placeholder="Add notes about this refund decision..."
                          value={selectedRefund?.id === refund.id ? adminNotes : ''}
                          onChange={(e) => {
                            setSelectedRefund(refund);
                            setAdminNotes(e.target.value);
                          }}
                          rows={3}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="destructive"
                          onClick={() => handleReject(refund.id)}
                          disabled={isPending}
                          className="flex-1"
                        >
                          Reject
                        </Button>
                        <Button
                          onClick={() => handleApprove(refund.id)}
                          disabled={isPending}
                          className="flex-1"
                        >
                          {isPending ? 'Processing...' : 'Approve & Process'}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
